import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Award, Plus, ArrowLeft, Search, Upload, Users, CheckCircle2, XCircle,
  ShieldCheck, ClipboardCheck, Trash2, Pencil, AlertTriangle, History, UserPlus, FileText,
  PenTool, Send, Mail, Ban, Copy, Download
} from 'lucide-react';
import { toast } from 'sonner';
import TemplateEditor from './TemplateEditor';
import CertDeliveryView from './CertDeliveryView';

const REQ_LABELS = {
  attendance: 'Attendance',
  survey: 'Survey / Evaluation',
  assessment: 'Assessment',
  admin_verification: 'Admin Verification'
};

const emptyTrainingForm = {
  title: '', description: '', date: '', hours: '', organization: 'DC Early Learning Collaborative',
  requirements: {
    attendance: { enabled: false, min_percent: 100 },
    survey: { enabled: false, label: '' },
    assessment: { enabled: false, min_score: null },
    admin_verification: { enabled: false, label: '' }
  },
  sessions: []
};

export const CertificatesTab = ({ API_URL, getAuthHeader }) => {
  const [trainings, setTrainings] = useState([]);
  const [selected, setSelected] = useState(null); // training id in detail view
  const [detail, setDetail] = useState(null); // {training, participants, counts}
  const [showForm, setShowForm] = useState(false);
  const [editTraining, setEditTraining] = useState(null);
  const [form, setForm] = useState(emptyTrainingForm);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [checked, setChecked] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newP, setNewP] = useState({ name: '', email: '' });
  const [auditLog, setAuditLog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState('trainings'); // trainings | templates
  const [templates, setTemplates] = useState([]);
  const [editorTpl, setEditorTpl] = useState(null);
  const [certs, setCerts] = useState([]);
  const [signers, setSigners] = useState([]);
  const [signRequests, setSignRequests] = useState([]);
  const [certChecked, setCertChecked] = useState([]);
  const [approveChecked, setApproveChecked] = useState([]);
  const [slotAssign, setSlotAssign] = useState({});
  const [showSignerForm, setShowSignerForm] = useState(false);
  const [editSigner, setEditSigner] = useState(null);
  const [signerForm, setSignerForm] = useState({ name: '', title: '', organization: 'DC Early Learning Collaborative', email: '' });

  const fetchSigners = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/cert/signers`, { headers: getAuthHeader() });
      if (r.ok) setSigners(await r.json());
    } catch { /* noop */ }
  }, [API_URL, getAuthHeader]);

  const fetchSignRequests = useCallback(async (tid) => {
    try {
      const r = await fetch(`${API_URL}/api/cert/trainings/${tid}/sign-requests`, { headers: getAuthHeader() });
      if (r.ok) setSignRequests(await r.json());
    } catch { /* noop */ }
  }, [API_URL, getAuthHeader]);

  const fetchTemplates = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/cert/templates`, { headers: getAuthHeader() });
      if (r.ok) setTemplates(await r.json());
    } catch { toast.error('Could not load templates'); }
  }, [API_URL, getAuthHeader]);

  const fetchCerts = useCallback(async (tid) => {
    try {
      const r = await fetch(`${API_URL}/api/cert/trainings/${tid}/certificates`, { headers: getAuthHeader() });
      if (r.ok) setCerts(await r.json());
    } catch { /* noop */ }
  }, [API_URL, getAuthHeader]);

  const fetchTrainings = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/cert/trainings`, { headers: getAuthHeader() });
      if (r.ok) setTrainings(await r.json());
    } catch { toast.error('Could not load trainings'); }
  }, [API_URL, getAuthHeader]);

  const fetchDetail = useCallback(async (tid) => {
    try {
      const r = await fetch(`${API_URL}/api/cert/trainings/${tid}/participants`, { headers: getAuthHeader() });
      if (r.ok) setDetail(await r.json());
    } catch { toast.error('Could not load participants'); }
  }, [API_URL, getAuthHeader]);

  useEffect(() => { fetchTrainings(); fetchTemplates(); fetchSigners(); }, [fetchTrainings, fetchTemplates, fetchSigners]);
  useEffect(() => { if (selected) { fetchDetail(selected); fetchCerts(selected); fetchSignRequests(selected); setChecked([]); setCertChecked([]); setApproveChecked([]); setAuditLog(null); } }, [selected, fetchDetail, fetchCerts, fetchSignRequests]);
  useEffect(() => {
    if (detail?.training) {
      const m = {};
      (detail.training.signers || []).forEach(a => { m[a.slot] = a.signer_id; });
      setSlotAssign(m);
    }
  }, [detail]);
  useEffect(() => { setChecked([]); }, [filter, search]);

  const api = async (method, path, body, isForm = false) => {
    const opts = { method, headers: isForm ? getAuthHeader() : { ...getAuthHeader(), 'Content-Type': 'application/json' } };
    if (body) opts.body = isForm ? body : JSON.stringify(body);
    const r = await fetch(`${API_URL}/api${path}`, opts);
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.detail || 'Request failed');
    return data;
  };

  const saveTraining = async () => {
    if (!form.title.trim()) { toast.error('Training title is required'); return; }
    setBusy(true);
    try {
      if (editTraining) {
        await api('PUT', `/cert/trainings/${editTraining}`, form);
        toast.success('Training updated');
        if (selected) fetchDetail(selected);
      } else {
        await api('POST', '/cert/trainings', form);
        toast.success('Training created');
      }
      setShowForm(false); setEditTraining(null); setForm(emptyTrainingForm);
      fetchTrainings();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const openEdit = (t) => {
    setForm({
      title: t.title, description: t.description || '', date: t.date || '', hours: t.hours || '',
      organization: t.organization || '', requirements: {
        attendance: { enabled: false, min_percent: 100, ...t.requirements?.attendance },
        survey: { enabled: false, label: '', ...t.requirements?.survey },
        assessment: { enabled: false, min_score: null, ...t.requirements?.assessment },
        admin_verification: { enabled: false, label: '', ...t.requirements?.admin_verification }
      }, sessions: t.sessions || []
    });
    setEditTraining(t.id); setShowForm(true);
  };

  const addParticipant = async () => {
    setBusy(true);
    try {
      await api('POST', `/cert/trainings/${selected}/participants`, newP);
      toast.success('Participant added');
      setShowAdd(false); setNewP({ name: '', email: '' });
      fetchDetail(selected);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const importRegs = async () => {
    setBusy(true);
    try {
      const d = await api('POST', `/cert/trainings/${selected}/participants/import-registrations`);
      toast.success(`Imported ${d.added} from Play Science registrations (${d.skipped} skipped)`);
      fetchDetail(selected);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const importCSV = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const d = await api('POST', `/cert/trainings/${selected}/participants/import-csv`, fd, true);
      toast.success(`Imported ${d.added} from CSV (${d.skipped} skipped)`);
      fetchDetail(selected);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const recordCompletion = async (pid, requirement, met, value = null, source = 'admin manual verification') => {
    try {
      await api('POST', `/cert/participants/${pid}/completion`, { requirement, met, value, source });
      fetchDetail(selected);
    } catch (e) { toast.error(e.message); }
  };

  const toggleSession = async (p, sessionId) => {
    const current = p.completion?.attendance?.session_ids || [];
    const next = current.includes(sessionId) ? current.filter(s => s !== sessionId) : [...current, sessionId];
    try {
      await api('POST', `/cert/participants/${p.id}/attendance`, { session_ids: next });
      fetchDetail(selected);
    } catch (e) { toast.error(e.message); }
  };

  const doOverride = async (p) => {
    const active = !p.override?.active;
    let reason = '';
    if (active) {
      reason = window.prompt('Eligibility override requires a reason (recorded in the audit log):');
      if (!reason || !reason.trim()) return;
    }
    try {
      await api('POST', `/cert/participants/${p.id}/override`, { active, reason: reason || 'Override removed' });
      toast.success(active ? 'Override recorded' : 'Override removed');
      fetchDetail(selected);
    } catch (e) { toast.error(e.message); }
  };

  const approve = async (ids, approveFlag) => {
    try {
      const d = await api('POST', `/cert/trainings/${selected}/participants/approve`, { participant_ids: ids, approve: approveFlag });
      toast.success(`${d.processed} ${approveFlag ? 'approved' : 'unapproved'}${d.skipped ? `, ${d.skipped} skipped (ineligible or has certificates)` : ''}`);
      setChecked([]);
      fetchDetail(selected);
    } catch (e) { toast.error(e.message); }
  };

  const editDisplayName = async (p) => {
    const name = window.prompt('Certificate display name (does not change the registration name):', p.display_name || p.name);
    if (!name || !name.trim() || name === p.display_name) return;
    try {
      await api('PUT', `/cert/participants/${p.id}`, { display_name: name });
      toast.success('Display name updated');
      fetchDetail(selected);
    } catch (e) { toast.error(e.message); }
  };

  const editEmail = async (p) => {
    const email = window.prompt('Corrected delivery email address:', p.email);
    if (!email || email.trim().toLowerCase() === p.email) return;
    const reason = window.prompt('Reason for the email change (required, recorded in audit log):');
    if (!reason || !reason.trim()) return;
    try {
      await api('PUT', `/cert/participants/${p.id}`, { email, reason });
      toast.success('Email updated — recipient approval reset for this participant');
      fetchDetail(selected);
    } catch (e) { toast.error(e.message); }
  };

  const removeParticipant = async (p) => {
    if (!window.confirm(`Remove ${p.name} from this training?`)) return;
    try {
      await api('DELETE', `/cert/participants/${p.id}`);
      toast.success('Participant removed');
      fetchDetail(selected);
    } catch (e) { toast.error(e.message); }
  };

  const loadAudit = async () => {
    try { setAuditLog(await api('GET', `/cert/trainings/${selected}/audit`)); }
    catch (e) { toast.error(e.message); }
  };

  const uploadTemplate = async (file) => {
    const name = window.prompt('Template name:', file.name.replace(/\.[^.]+$/, ''));
    if (name === null) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await fetch(`${API_URL}/api/cert/templates?name=${encodeURIComponent(name || file.name)}`, {
        method: 'POST', headers: getAuthHeader(), body: fd
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Upload failed');
      toast.success(`Template uploaded${d.low_res ? ' — warning: low resolution' : ''}`);
      fetchTemplates();
      setEditorTpl(d);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try { await api('DELETE', `/cert/templates/${tpl.id}`); toast.success('Template deleted'); fetchTemplates(); }
    catch (e) { toast.error(e.message); }
  };

  const assignTemplate = async (templateId) => {
    try {
      await api('PUT', `/cert/trainings/${selected}/template`, { template_id: templateId || null });
      toast.success(templateId ? 'Template assigned' : 'Template unassigned');
      fetchDetail(selected);
    } catch (e) { toast.error(e.message); }
  };

  const generateCerts = async (ids) => {
    setBusy(true);
    try {
      const d = await api('POST', `/cert/trainings/${selected}/generate`, { participant_ids: ids });
      const msgs = [`${d.generated.length} generated`];
      if (d.skipped.length) msgs.push(`${d.skipped.length} skipped (${[...new Set(d.skipped.map(s => s.reason))].join('; ')})`);
      if (d.errors.length) msgs.push(`${d.errors.length} failed`);
      (d.errors.length ? toast.error : toast.success)(msgs.join(' · '));
      fetchCerts(selected);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const viewCertPdf = async (cert) => {
    try {
      const r = await fetch(`${API_URL}/api/cert/certificates/${cert.id}/pdf`, { headers: getAuthHeader() });
      if (!r.ok) throw new Error('Could not load PDF');
      window.open(URL.createObjectURL(await r.blob()), '_blank', 'noopener');
    } catch (e) { toast.error(e.message); }
  };

  const revokeCert = async (cert) => {
    const reason = window.prompt(`Revoke ${cert.cert_number}? Enter a reason (required):`);
    if (!reason || !reason.trim()) return;
    try { await api('POST', `/cert/certificates/${cert.id}/revoke`, { reason }); toast.success('Certificate revoked'); fetchCerts(selected); }
    catch (e) { toast.error(e.message); }
  };

  // ---------- Stage 3: signers & signing ----------
  const saveSigner = async () => {
    if (!signerForm.name.trim() || !signerForm.email.trim()) { toast.error('Name and email are required'); return; }
    try {
      if (editSigner) { await api('PUT', `/cert/signers/${editSigner}`, signerForm); toast.success('Signer updated'); }
      else { await api('POST', '/cert/signers', signerForm); toast.success('Signer added'); }
      setShowSignerForm(false); setEditSigner(null);
      setSignerForm({ name: '', title: '', organization: 'DC Early Learning Collaborative', email: '' });
      fetchSigners();
    } catch (e) { toast.error(e.message); }
  };

  const deleteSigner = async (s) => {
    if (!window.confirm(`Remove ${s.name} from the signer directory?`)) return;
    try { await api('DELETE', `/cert/signers/${s.id}`); toast.success('Signer removed'); fetchSigners(); }
    catch (e) { toast.error(e.message); }
  };

  const saveSigners = async () => {
    const assignments = Object.entries(slotAssign).filter(([, v]) => v).map(([slot, signer_id]) => ({ slot: Number(slot), signer_id }));
    try {
      await api('PUT', `/cert/trainings/${selected}/signers`, { assignments });
      toast.success('Signers saved for this training');
      fetchDetail(selected);
    } catch (e) { toast.error(e.message); }
  };

  const requestSignatures = async () => {
    setBusy(true);
    try {
      const d = await api('POST', `/cert/trainings/${selected}/sign-requests`, { certificate_ids: certChecked });
      toast.success(d.requests.map(r => `${r.email_status === 'sent' ? 'Email sent to' : '⚠ EMAIL FAILED for'} ${r.signer_name} — ${r.cert_count} cert${r.cert_count !== 1 ? 's' : ''}`).join(' · '));
      setCertChecked([]);
      fetchSignRequests(selected); fetchCerts(selected);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const remindRequest = async (r) => {
    try {
      await api('POST', `/cert/sign-requests/${r.id}/remind`);
      toast.success(`Fresh signing link emailed to ${r.signer?.email} (old link invalidated)`);
      fetchSignRequests(selected);
    } catch (e) { toast.error(e.message); }
  };

  const cancelRequest = async (r) => {
    if (!window.confirm(`Cancel the signature request for ${r.signer?.name}? The emailed link will stop working.`)) return;
    try {
      await api('POST', `/cert/sign-requests/${r.id}/cancel`);
      toast.success('Request cancelled');
      fetchSignRequests(selected); fetchCerts(selected);
    } catch (e) { toast.error(e.message); }
  };

  // ---------- Stage 4: final approval & email delivery ----------
  const pollRef = useRef(null);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const pollDelivery = useCallback((tid) => {
    clearInterval(pollRef.current);
    let n = 0;
    pollRef.current = setInterval(async () => {
      n += 1;
      try {
        const r = await fetch(`${API_URL}/api/cert/trainings/${tid}/certificates`, { headers: getAuthHeader() });
        if (!r.ok) { clearInterval(pollRef.current); return; }
        const data = await r.json();
        setCerts(data);
        if (n >= 20 || !data.some(c => c.delivery?.status === 'queued')) clearInterval(pollRef.current);
      } catch { clearInterval(pollRef.current); }
    }, 3000);
  }, [API_URL, getAuthHeader]);

  const approveAndSend = async () => {
    if (!window.confirm(`Approve and email ${approveChecked.length} certificate(s) to participants now? Each gets the PDF attached plus a personal download link.`)) return;
    setBusy(true);
    try {
      const d = await api('POST', `/cert/trainings/${selected}/approve-send`, { certificate_ids: approveChecked });
      toast.success(`${d.approved} certificate(s) approved — emails are being sent now`);
      setApproveChecked([]);
      fetchCerts(selected);
      pollDelivery(selected);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const resendCertEmail = async (c) => {
    if (!window.confirm(`Resend the certificate email to ${c.email}?`)) return;
    try {
      await api('POST', `/cert/certificates/${c.id}/resend-email`);
      toast.success(`Email re-queued for ${c.email}`);
      fetchCerts(selected);
      pollDelivery(selected);
    } catch (e) { toast.error(e.message); }
  };

  // ---------- Archive, replace & duplicate ----------
  const [archive, setArchive] = useState({ total: 0, certificates: [] });
  const [archiveFilters, setArchiveFilters] = useState({ search: '', status: '', training_id: '' });

  const fetchArchive = useCallback(async (f) => {
    try {
      const qs = new URLSearchParams({ search: f.search, status: f.status, training_id: f.training_id, limit: '200' });
      const r = await fetch(`${API_URL}/api/cert/certificates/archive?${qs}`, { headers: getAuthHeader() });
      if (r.ok) setArchive(await r.json());
    } catch { /* noop */ }
  }, [API_URL, getAuthHeader]);

  useEffect(() => {
    if (view !== 'archive') return undefined;
    const t = setTimeout(() => fetchArchive(archiveFilters), 300);
    return () => clearTimeout(t);
  }, [view, archiveFilters, fetchArchive]);

  const exportArchiveCsv = async () => {
    try {
      const qs = new URLSearchParams({ search: archiveFilters.search, status: archiveFilters.status, training_id: archiveFilters.training_id });
      const r = await fetch(`${API_URL}/api/cert/certificates/archive.csv?${qs}`, { headers: getAuthHeader() });
      if (!r.ok) throw new Error('Export failed');
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a');
      a.href = url; a.download = 'dcelc_certificates.csv'; a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (e) { toast.error(e.message); }
  };

  const [replacingId, setReplacingId] = useState(null);
  const replaceCert = async (c) => {
    if (!window.confirm(`Reissue a corrected certificate for ${c.display_name}? Collected signatures are carried over and, if fully signed, it is emailed to the participant immediately.\n\nTip: if the correction is a name fix, update the participant's certificate name BEFORE reissuing.`)) return;
    setReplacingId(c.id);
    try {
      const d = await api('POST', `/cert/certificates/${c.id}/replace`);
      toast.success(d.emailed
        ? `${d.cert_number} issued with ${d.carried_signatures} signature(s) and emailed to ${c.email}`
        : `${d.cert_number} issued — it still needs signatures before it can be sent`);
      fetchCerts(selected);
      if (d.emailed) pollDelivery(selected);
    } catch (e) { toast.error(e.message); } finally { setReplacingId(null); }
  };

  const duplicateTraining = async (t) => {
    if (!window.confirm(`Duplicate "${t.title}"? The copy includes its requirements, template and signers — participants are not copied.`)) return;
    try {
      const d = await api('POST', `/cert/trainings/${t.id}/duplicate`);
      toast.success(`Created "${d.title}"`);
      fetchTrainings();
    } catch (e) { toast.error(e.message); }
  };

  // ---------- Training form modal ----------
  const reqField = (key, extra) => (
    <div key={key} className="border border-slate-200 rounded-lg p-3">
      <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
        <input
          type="checkbox"
          checked={form.requirements[key].enabled}
          onChange={(e) => setForm(f => ({ ...f, requirements: { ...f.requirements, [key]: { ...f.requirements[key], enabled: e.target.checked } } }))}
          data-testid={`req-toggle-${key}`}
          className="h-4 w-4"
        />
        {REQ_LABELS[key]}
      </label>
      {form.requirements[key].enabled && extra}
    </div>
  );

  if (editorTpl) {
    return <TemplateEditor API_URL={API_URL} getAuthHeader={getAuthHeader} template={editorTpl}
      onBack={() => { setEditorTpl(null); fetchTemplates(); }}
      onSaved={() => fetchTemplates()} />;
  }

  if (showForm) {
    return (
      <div className="max-w-3xl space-y-5" data-testid="training-form">
        <button onClick={() => { setShowForm(false); setEditTraining(null); setForm(emptyTrainingForm); }} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-teal-700" data-testid="training-form-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h2 className="text-2xl font-bold text-slate-800">{editTraining ? 'Edit Training' : 'Create Training'}</h2>
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="tr-title">Training Title *</label>
              <input id="tr-title" data-testid="training-title-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="tr-date">Date(s)</label>
              <input id="tr-date" data-testid="training-date-input" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} placeholder="e.g., October 12, 2026" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="tr-hours">Training Hours / PD Info</label>
              <input id="tr-hours" value={form.hours} onChange={e => setForm(f => ({ ...f, hours: e.target.value }))} placeholder="e.g., 6 PD hours" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="tr-org">Issuing Organization</label>
              <input id="tr-org" value={form.organization} onChange={e => setForm(f => ({ ...f, organization: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="tr-desc">Description</label>
              <textarea id="tr-desc" rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide mb-2">Completion Requirements</h3>
            <p className="text-xs text-slate-500 mb-3">All selected requirements must be satisfied before a participant becomes eligible. Meeting them does not automatically issue a certificate — recipient approval is still required.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {reqField('attendance', (
                <div className="mt-2">
                  <label className="text-xs text-slate-500" htmlFor="req-att-min">Minimum attendance %</label>
                  <input id="req-att-min" type="number" min="1" max="100" value={form.requirements.attendance.min_percent ?? 100}
                    onChange={(e) => setForm(f => ({ ...f, requirements: { ...f.requirements, attendance: { ...f.requirements.attendance, min_percent: Number(e.target.value) } } }))}
                    className="w-full border border-slate-200 rounded px-2 py-1 text-sm mt-1" />
                </div>
              ))}
              {reqField('survey', (
                <div className="mt-2">
                  <label className="text-xs text-slate-500" htmlFor="req-sv-label">Survey name</label>
                  <input id="req-sv-label" value={form.requirements.survey.label || ''} placeholder="e.g., Post-training evaluation"
                    onChange={(e) => setForm(f => ({ ...f, requirements: { ...f.requirements, survey: { ...f.requirements.survey, label: e.target.value } } }))}
                    className="w-full border border-slate-200 rounded px-2 py-1 text-sm mt-1" />
                </div>
              ))}
              {reqField('assessment', (
                <div className="mt-2">
                  <label className="text-xs text-slate-500" htmlFor="req-as-min">Minimum passing score (optional)</label>
                  <input id="req-as-min" type="number" value={form.requirements.assessment.min_score ?? ''}
                    onChange={(e) => setForm(f => ({ ...f, requirements: { ...f.requirements, assessment: { ...f.requirements.assessment, min_score: e.target.value === '' ? null : Number(e.target.value) } } }))}
                    className="w-full border border-slate-200 rounded px-2 py-1 text-sm mt-1" />
                </div>
              ))}
              {reqField('admin_verification', (
                <div className="mt-2">
                  <label className="text-xs text-slate-500" htmlFor="req-av-label">What is being verified?</label>
                  <input id="req-av-label" value={form.requirements.admin_verification.label || ''} placeholder="e.g., Submitted reflection form"
                    onChange={(e) => setForm(f => ({ ...f, requirements: { ...f.requirements, admin_verification: { ...f.requirements.admin_verification, label: e.target.value } } }))}
                    className="w-full border border-slate-200 rounded px-2 py-1 text-sm mt-1" />
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide mb-2">Sessions (for attendance tracking)</h3>
            {form.sessions.map((s, i) => (
              <div key={s.id} className="flex items-center gap-2 mb-2">
                <input aria-label={`Session ${i + 1} title`} value={s.title} onChange={e => setForm(f => ({ ...f, sessions: f.sessions.map(x => x.id === s.id ? { ...x, title: e.target.value } : x) }))} className="flex-1 border border-slate-200 rounded px-2 py-1 text-sm" />
                <input aria-label={`Session ${i + 1} date`} value={s.date || ''} placeholder="Date" onChange={e => setForm(f => ({ ...f, sessions: f.sessions.map(x => x.id === s.id ? { ...x, date: e.target.value } : x) }))} className="w-40 border border-slate-200 rounded px-2 py-1 text-sm" />
                <button aria-label={`Remove session ${i + 1}`} onClick={() => setForm(f => ({ ...f, sessions: f.sessions.filter(x => x.id !== s.id) }))} className="text-rose-500 hover:text-rose-700 p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={() => setForm(f => ({ ...f, sessions: [...f.sessions, { id: crypto.randomUUID(), title: `Session ${f.sessions.length + 1}`, date: '' }] }))} className="text-xs font-semibold text-teal-700 hover:text-teal-900" data-testid="add-session-btn">+ Add Session</button>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => { setShowForm(false); setEditTraining(null); setForm(emptyTrainingForm); }} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
            <button onClick={saveTraining} disabled={busy} data-testid="save-training-btn" className="px-5 py-2 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50">
              {busy ? 'Saving…' : editTraining ? 'Save Changes' : 'Create Training'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Training detail ----------
  if (selected && detail) {
    const t = detail.training;
    const enabledReqs = Object.keys(REQ_LABELS).filter(k => t.requirements?.[k]?.enabled);
    const filtered = detail.participants
      .filter(p => filter === 'all' || (filter === 'eligible' && p.eligible && !p.recipient_approved?.approved) || (filter === 'approved' && p.recipient_approved?.approved) || (filter === 'ineligible' && !p.eligible))
      .filter(p => {
        const q = search.toLowerCase();
        return !q || [p.name, p.email, p.display_name].some(v => (v || '').toLowerCase().includes(q));
      });
    const eligibleUnapproved = detail.participants.filter(p => p.eligible && !p.recipient_approved?.approved).map(p => p.id);
    const signable = certs.filter(c => ['generated', 'awaiting_signatures', 'partially_signed'].includes(c.status));
    const assignedTpl = templates.find(tp => tp.id === t.template_id);
    const slotSet = [...new Set((assignedTpl?.fields || []).filter(f => ['signature', 'signer_name', 'signer_title'].includes(f.type)).map(f => f.signer_slot || 1))].sort((a, b) => a - b);
    const signerSlots = slotSet.length ? slotSet : [1];
    const assignedSlots = signerSlots.filter(s => slotAssign[s]);

    return (
      <div className="space-y-5" data-testid="training-detail">
        <button onClick={() => { setSelected(null); setDetail(null); fetchTrainings(); }} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-teal-700" data-testid="back-to-trainings">
          <ArrowLeft className="w-4 h-4" /> All Trainings
        </button>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-bold text-slate-800">{t.title}</h2>
            <p className="text-sm text-slate-500">{[t.date, t.hours, t.organization].filter(Boolean).join(' · ')}</p>
            <p className="text-xs text-slate-400 mt-1">Requirements: {enabledReqs.length ? enabledReqs.map(k => REQ_LABELS[k]).join(', ') : 'None configured — all participants eligible'}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => openEdit(t)} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-slate-200 rounded-lg hover:border-teal-400 text-slate-700" data-testid="edit-training-btn"><Pencil className="w-3.5 h-3.5" /> Edit Training</button>
            <button onClick={() => setShowAdd(true)} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-slate-200 rounded-lg hover:border-teal-400 text-slate-700" data-testid="add-participant-btn"><UserPlus className="w-3.5 h-3.5" /> Add Participant</button>
            <button onClick={importRegs} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-slate-200 rounded-lg hover:border-teal-400 text-slate-700 disabled:opacity-50" data-testid="import-registrations-btn"><Users className="w-3.5 h-3.5" /> Import Play Science Regs</button>
            <label className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-slate-200 rounded-lg hover:border-teal-400 text-slate-700 cursor-pointer">
              <Upload className="w-3.5 h-3.5" /> Import CSV
              <input type="file" accept=".csv" className="hidden" data-testid="import-csv-input" onChange={e => { const f = e.target.files?.[0]; if (f) importCSV(f); e.target.value = ''; }} />
            </label>
            <button onClick={loadAudit} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-slate-200 rounded-lg hover:border-teal-400 text-slate-700" data-testid="audit-log-btn"><History className="w-3.5 h-3.5" /> Audit Log</button>
          </div>
        </div>

        {/* Summary counts */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            ['Total Participants', detail.counts.total, 'text-slate-800'],
            ['Eligible', detail.counts.eligible, 'text-emerald-700'],
            ['Approved Recipients', detail.counts.approved, 'text-teal-700'],
            ['Not Yet Eligible', detail.counts.ineligible, 'text-amber-700']
          ].map(([label, val, color]) => (
            <div key={label} className="bg-white border border-slate-200 rounded-xl p-4">
              <p className={`text-2xl font-bold ${color}`} data-testid={`count-${label.toLowerCase().replace(/\s+/g, '-')}`}>{val}</p>
              <p className="text-xs text-slate-500">{label}</p>
            </div>
          ))}
        </div>

        {/* Certificate setup: template + generation */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4 flex-wrap" data-testid="certificate-setup">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="tpl-select">Certificate Template</label>
            <select id="tpl-select" value={t.template_id || ''} onChange={e => assignTemplate(e.target.value)} data-testid="template-assign-select" className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[240px]">
              <option value="">— No template assigned —</option>
              {templates.map(tp => <option key={tp.id} value={tp.id}>{tp.name} (v{tp.version}, {tp.orientation})</option>)}
            </select>
          </div>
          {(() => {
            const activeCertPids = new Set(certs.filter(c => !['revoked', 'replaced'].includes(c.status)).map(c => c.participant_id));
            const ready = detail.participants.filter(p => p.recipient_approved?.approved && !activeCertPids.has(p.id));
            const tpl = templates.find(tp => tp.id === t.template_id);
            const issues = [];
            if (!t.template_id) issues.push('no template assigned');
            else if (tpl && !(tpl.fields || []).some(f => f.type === 'participant_name')) issues.push('template has no participant name field');
            return (
              <div className="flex items-end gap-3 flex-wrap">
                <button onClick={() => generateCerts(ready.map(p => p.id))} disabled={busy || ready.length === 0 || issues.length > 0}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-50" data-testid="generate-certs-btn">
                  <Award className="w-4 h-4" /> Generate Certificates ({ready.length} approved without one)
                </button>
                {issues.length > 0 && <p className="text-xs text-amber-700 font-semibold">⚠ {issues.join(', ')}</p>}
                {t.template_id && tpl && (
                  <button onClick={() => setEditorTpl(tpl)} className="text-xs font-semibold text-teal-700 hover:underline" data-testid="open-assigned-template">Edit template layout →</button>
                )}
              </div>
            );
          })()}
        </div>

        {/* Signers & signature requests */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4" data-testid="signers-panel">
          <div className="flex items-center gap-2">
            <PenTool className="w-4 h-4 text-violet-700" />
            <h3 className="text-sm font-bold text-slate-800">Signers &amp; Electronic Signing</h3>
            <span className="text-xs text-slate-400">— signers get a secure email link, no account needed (expires in 7 days)</span>
          </div>
          <div className="flex items-end gap-3 flex-wrap">
            {signerSlots.map(slot => (
              <div key={slot}>
                <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor={`signer-slot-${slot}`}>Signature slot {slot}</label>
                <select id={`signer-slot-${slot}`} data-testid={`signer-slot-select-${slot}`} value={slotAssign[slot] || ''}
                  onChange={e => setSlotAssign(m => ({ ...m, [slot]: e.target.value }))}
                  className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-500 min-w-[220px]">
                  <option value="">— No signer —</option>
                  {signers.map(s => <option key={s.id} value={s.id}>{s.name}{s.title ? ` — ${s.title}` : ''}</option>)}
                </select>
              </div>
            ))}
            <button onClick={saveSigners} data-testid="save-signers-btn" className="px-4 py-2 text-xs font-bold text-white bg-violet-600 hover:bg-violet-700 rounded-lg">Save Signers</button>
            <button onClick={requestSignatures} disabled={busy || certChecked.length === 0 || assignedSlots.length === 0}
              data-testid="request-signatures-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50">
              <Send className="w-3.5 h-3.5" /> Request Signatures ({certChecked.length} selected)
            </button>
          </div>
          {signers.length === 0 && <p className="text-xs text-amber-700 font-semibold">No signers in the directory yet — add them under the "Signers" view on the main Certificates screen.</p>}
          {certChecked.length === 0 && signable.length > 0 && assignedSlots.length > 0 && <p className="text-xs text-slate-400">Tick certificates in the table below, then request signatures from the assigned signer{assignedSlots.length > 1 ? 's' : ''}.</p>}

          {signRequests.length > 0 && (
            <div className="border-t border-slate-100 pt-3" data-testid="sign-requests-list">
              <h4 className="text-xs font-bold text-slate-600 uppercase mb-2">Signature Requests</h4>
              <div className="space-y-2">
                {signRequests.map(r => (
                  <div key={r.id} className="flex items-center justify-between flex-wrap gap-2 text-xs border border-slate-100 rounded-lg px-3 py-2" data-testid={`sign-request-${r.id}`}>
                    <div>
                      <span className="font-semibold text-slate-800">{r.signer?.name}</span>
                      <span className="text-slate-500"> · slot {r.slot} · {r.signer?.email} · {(r.certificate_ids || []).length} cert{(r.certificate_ids || []).length !== 1 ? 's' : ''}</span>
                      {r.email_status === 'failed' && <span className="text-rose-600 font-bold"> · EMAIL FAILED</span>}
                      <span className="text-slate-400 block">
                        Requested {new Date(r.created_at).toLocaleDateString()} · expires {new Date(r.expires_at).toLocaleDateString()}
                        {r.reminders?.length ? ` · ${r.reminders.length} reminder${r.reminders.length !== 1 ? 's' : ''}` : ''}
                        {r.status === 'signed' && r.signed_at ? ` · signed ${new Date(r.signed_at).toLocaleDateString()} (${r.method})` : ''}
                      </span>
                      {r.status === 'declined' && r.decline_reason && <span className="text-rose-600 block">Declined: {r.decline_reason}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-block px-2 py-1 rounded-full text-[10px] font-bold uppercase ${
                        r.status === 'signed' ? 'bg-emerald-100 text-emerald-800' :
                        r.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                        r.status === 'declined' ? 'bg-rose-100 text-rose-700' :
                        'bg-slate-100 text-slate-600'
                      }`} data-testid={`sign-request-status-${r.id}`}>{r.status}</span>
                      {(r.status === 'pending' || r.status === 'expired') && (
                        <>
                          {r.status === 'pending' && (
                            <button onClick={() => remindRequest(r)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-teal-700 hover:underline" data-testid={`remind-request-${r.id}`}>
                              <Mail className="w-3 h-3" /> Resend Link
                            </button>
                          )}
                          {r.status === 'expired' && (
                            <button onClick={() => remindRequest(r)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-teal-700 hover:underline" data-testid={`remind-request-${r.id}`}>
                              <Mail className="w-3 h-3" /> Send New Link
                            </button>
                          )}
                          <button onClick={() => cancelRequest(r)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-600 hover:underline" data-testid={`cancel-request-${r.id}`}>
                            <Ban className="w-3 h-3" /> Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Certificates list */}
        {certs.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="certificates-list">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <Award className="w-4 h-4 text-emerald-700" />
              <h3 className="text-sm font-bold text-slate-800">Certificates ({certs.length})</h3>
              <span className="text-xs text-slate-400">Certificate status is tracked separately from email delivery</span>
            </div>
            {(() => {
              const awaiting = certs.filter(c => c.status === 'signed');
              if (!awaiting.length) return null;
              return (
                <div className="border border-emerald-200 bg-emerald-50/50 rounded-xl p-4 mb-4" data-testid="final-approval-panel">
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-emerald-700" />
                      <h4 className="text-sm font-bold text-slate-800">Final Approval — {awaiting.length} certificate{awaiting.length !== 1 ? 's' : ''} fully signed &amp; awaiting your approval</h4>
                    </div>
                    <button onClick={approveAndSend} disabled={busy || approveChecked.length === 0} data-testid="approve-send-btn"
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-50">
                      <Send className="w-3.5 h-3.5" /> Approve &amp; Send ({approveChecked.length})
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mb-2">Nothing is emailed to participants until you approve. Each participant receives their PDF attached plus a personal download link.</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer">
                      <input type="checkbox" checked={approveChecked.length === awaiting.length} onChange={e => setApproveChecked(e.target.checked ? awaiting.map(c => c.id) : [])} data-testid="approve-select-all" /> Select all
                    </label>
                    {awaiting.map(c => (
                      <label key={c.id} className="inline-flex items-center gap-1.5 text-xs bg-white border border-emerald-100 rounded-lg px-2.5 py-1.5 cursor-pointer">
                        <input type="checkbox" checked={approveChecked.includes(c.id)} onChange={e => setApproveChecked(v => e.target.checked ? [...v, c.id] : v.filter(x => x !== c.id))} data-testid={`approve-check-${c.id}`} />
                        <span className="font-medium text-slate-700">{c.display_name}</span>
                        <span className="text-slate-400 font-mono">({c.cert_number})</span>
                        <button type="button" onClick={(ev) => { ev.preventDefault(); viewCertPdf(c); }} className="text-teal-700 underline" data-testid={`approve-preview-${c.id}`}>preview</button>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })()}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                  <tr>
                    <th className="py-2 px-3">
                      <input type="checkbox" aria-label="Select all certificates for signing" data-testid="select-all-certs"
                        checked={certChecked.length === signable.length && signable.length > 0}
                        onChange={e => setCertChecked(e.target.checked ? signable.map(c => c.id) : [])} />
                    </th>
                    <th className="text-left py-2 px-3">Certificate #</th>
                    <th className="text-left py-2 px-3">Participant</th>
                    <th className="text-center py-2 px-3">Status</th>
                    <th className="text-center py-2 px-3">Signatures</th>
                    <th className="text-center py-2 px-3">Email Delivery</th>
                    <th className="text-center py-2 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {certs.map(c => (
                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`cert-row-${c.id}`}>
                      <td className="py-2.5 px-3">
                        {['generated', 'awaiting_signatures', 'partially_signed'].includes(c.status) && (
                          <input type="checkbox" aria-label={`Select certificate ${c.cert_number}`} data-testid={`cert-check-${c.id}`}
                            checked={certChecked.includes(c.id)}
                            onChange={e => setCertChecked(v => e.target.checked ? [...v, c.id] : v.filter(x => x !== c.id))} />
                        )}
                      </td>
                      <td className="py-2.5 px-3 font-mono text-xs text-slate-700">{c.cert_number}<p className="text-[9px] text-slate-400">v{c.version} · tpl v{c.template_version}</p>{c.replaced_by && <p className="text-[9px] text-amber-600 font-sans font-semibold" data-testid={`replaced-by-${c.id}`}>replaced by {certs.find(x => x.id === c.replaced_by)?.cert_number || 'newer certificate'}</p>}</td>
                      <td className="py-2.5 px-3"><p className="font-medium text-slate-800">{c.display_name}</p><p className="text-xs text-slate-500">{c.email}</p></td>
                      <td className="py-2.5 px-3 text-center">
                        <span className={`inline-block px-2 py-1 rounded-full text-[10px] font-bold uppercase ${
                          c.status === 'revoked' ? 'bg-rose-100 text-rose-700' :
                          c.status === 'sent' ? 'bg-emerald-100 text-emerald-800' :
                          c.status === 'approved' ? 'bg-teal-100 text-teal-800' :
                          c.status === 'awaiting_final_approval' ? 'bg-amber-100 text-amber-800' :
                          c.status === 'awaiting_signatures' ? 'bg-violet-100 text-violet-800' :
                          'bg-slate-100 text-slate-700'
                        }`}>{c.status.replace(/_/g, ' ')}</span>
                      </td>
                      <td className="py-2.5 px-3 text-center text-xs text-slate-500" title={(c.signatures || []).map(s => `${s.signer_name} (slot ${s.slot}): ${s.status}`).join('\n')}>{(c.signatures || []).filter(s => s.status === 'signed').length}/{(c.signatures || []).filter(s => ['pending', 'signed'].includes(s.status)).length || '—'}</td>
                      <td className="py-2.5 px-3 text-center text-xs" data-testid={`delivery-status-${c.id}`}>
                        {c.delivery ? (
                          <span className={`inline-block px-2 py-1 rounded-full text-[10px] font-bold uppercase ${
                            c.delivery.status === 'sent' ? 'bg-emerald-100 text-emerald-800' :
                            c.delivery.status === 'failed' ? 'bg-rose-100 text-rose-700' :
                            'bg-amber-100 text-amber-800'
                          }`} title={(c.email_log || []).map(l => `${(l.at || '').slice(0, 16).replace('T', ' ')} — ${l.status} (${l.type}) to ${l.to}${l.error ? ': ' + l.error : ''}`).join('\n')}>
                            {c.delivery.status}{c.delivery.attempts > 1 ? ` ×${c.delivery.attempts}` : ''}
                          </span>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="py-2.5 px-3 text-center whitespace-nowrap">
                        <button onClick={() => viewCertPdf(c)} className="text-xs font-semibold text-teal-700 hover:underline mr-3" data-testid={`view-cert-${c.id}`}>View PDF</button>
                        {c.status === 'approved' && (c.delivery?.status !== 'queued' || (c.delivery?.queued_at && Date.now() - new Date(c.delivery.queued_at).getTime() > 3 * 60 * 1000)) && (
                          <button onClick={() => resendCertEmail(c)} className="text-xs font-semibold text-indigo-600 hover:underline mr-3" data-testid={`resend-email-${c.id}`}>Resend Email</button>
                        )}
                        {c.status === 'revoked' && !c.replaced_by
                          && !certs.some(x => x.participant_id === c.participant_id && !['revoked', 'replaced'].includes(x.status))
                          && certs.find(x => x.participant_id === c.participant_id && x.status === 'revoked' && !x.replaced_by)?.id === c.id && (
                          <button onClick={() => replaceCert(c)} disabled={!!replacingId} className="text-xs font-semibold text-amber-700 hover:underline mr-3 disabled:opacity-50" data-testid={`replace-cert-${c.id}`}>{replacingId === c.id ? 'Reissuing…' : 'Reissue'}</button>
                        )}
                        {c.status !== 'revoked' && <button onClick={() => revokeCert(c)} className="text-xs font-semibold text-rose-600 hover:underline" data-testid={`revoke-cert-${c.id}`}>Revoke</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Filters + bulk actions */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search participants…" aria-label="Search participants" data-testid="participant-search" className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>
          <select value={filter} onChange={e => setFilter(e.target.value)} aria-label="Filter participants" data-testid="participant-filter" className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500">
            <option value="all">All</option>
            <option value="eligible">Eligible (awaiting approval)</option>
            <option value="approved">Approved recipients</option>
            <option value="ineligible">Not yet eligible</option>
          </select>
          {checked.length > 0 && (
            <>
              <button onClick={() => approve(checked, true)} className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-teal-600 hover:bg-teal-700 rounded-lg" data-testid="bulk-approve-btn">
                <ShieldCheck className="w-4 h-4" /> Approve Selected ({checked.length})
              </button>
              <button onClick={() => approve(checked, false)} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-rose-200 text-rose-700 rounded-lg hover:bg-rose-50" data-testid="bulk-unapprove-btn">
                Unapprove
              </button>
            </>
          )}
          {eligibleUnapproved.length > 0 && checked.length === 0 && (
            <button onClick={() => approve(eligibleUnapproved, true)} className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg" data-testid="approve-all-eligible-btn">
              <ShieldCheck className="w-4 h-4" /> Approve All Eligible ({eligibleUnapproved.length})
            </button>
          )}
        </div>

        {/* Audit log panel */}
        {auditLog && (
          <div className="bg-white border border-slate-200 rounded-xl p-4 max-h-72 overflow-y-auto" data-testid="audit-log-panel">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-slate-800">Audit Log</h3>
              <button onClick={() => setAuditLog(null)} className="text-xs text-slate-500 hover:text-slate-800">Close</button>
            </div>
            {auditLog.length === 0 ? <p className="text-xs text-slate-500">No audit entries yet.</p> : auditLog.map(a => (
              <div key={a.id} className="text-xs border-b border-slate-100 py-1.5">
                <span className="font-semibold text-slate-700">{a.action.replace(/_/g, ' ')}</span>
                <span className="text-slate-500"> — {a.actor} · {new Date(a.at).toLocaleString()}</span>
                <span className="text-slate-400 block truncate">{JSON.stringify(a.details)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Add participant inline form */}
        {showAdd && (
          <div className="bg-white border border-teal-200 rounded-xl p-4 flex items-end gap-3 flex-wrap" data-testid="add-participant-form">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="np-name">Full Name</label>
              <input id="np-name" data-testid="new-participant-name" value={newP.name} onChange={e => setNewP(v => ({ ...v, name: e.target.value }))} className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-56" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="np-email">Email</label>
              <input id="np-email" data-testid="new-participant-email" type="email" value={newP.email} onChange={e => setNewP(v => ({ ...v, email: e.target.value }))} className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-64" />
            </div>
            <button onClick={addParticipant} disabled={busy} data-testid="save-participant-btn" className="px-4 py-2 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50">Add</button>
            <button onClick={() => setShowAdd(false)} className="px-3 py-2 text-sm text-slate-500">Cancel</button>
          </div>
        )}

        {/* Participants table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {filtered.length === 0 ? (
            <div className="p-10 text-center text-slate-500">
              <Users className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">{detail.participants.length === 0 ? 'No participants yet. Add manually, import Play Science registrations, or upload a CSV.' : 'No participants match the current filter.'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                  <tr>
                    <th className="py-2 px-3">
                      <input type="checkbox" aria-label="Select all" checked={checked.length === filtered.length && filtered.length > 0}
                        onChange={e => setChecked(e.target.checked ? filtered.map(p => p.id) : [])} data-testid="select-all-participants" />
                    </th>
                    <th className="text-left py-2 px-3">Participant</th>
                    <th className="text-left py-2 px-3">Cert Name</th>
                    {enabledReqs.map(k => <th key={k} className="text-center py-2 px-3">{REQ_LABELS[k]}</th>)}
                    <th className="text-center py-2 px-3">Eligibility</th>
                    <th className="text-center py-2 px-3">Recipient Approval</th>
                    <th className="text-center py-2 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(p => (
                    <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/60 align-top" data-testid={`participant-row-${p.id}`}>
                      <td className="py-2.5 px-3">
                        <input type="checkbox" aria-label={`Select ${p.name}`} checked={checked.includes(p.id)}
                          onChange={e => setChecked(c => e.target.checked ? [...c, p.id] : c.filter(x => x !== p.id))} />
                      </td>
                      <td className="py-2.5 px-3">
                        <p className="font-medium text-slate-800">{p.name}</p>
                        <p className="text-xs text-slate-500">{p.email}</p>
                        <p className="text-[10px] text-slate-400 uppercase">{(p.source || '').replace(/_/g, ' ')}</p>
                      </td>
                      <td className="py-2.5 px-3">
                        <button onClick={() => editDisplayName(p)} className="text-slate-700 hover:text-teal-700 underline decoration-dotted underline-offset-2 text-left" data-testid={`edit-display-name-${p.id}`}>
                          {p.display_name || p.name}
                        </button>
                      </td>
                      {enabledReqs.map(k => {
                        const pr = p.progress[k];
                        return (
                          <td key={k} className="py-2.5 px-3 text-center">
                            {k === 'attendance' && (t.sessions || []).length > 0 ? (
                              <div className="flex flex-col items-center gap-1">
                                <span className={`text-xs font-semibold ${pr.met ? 'text-emerald-700' : 'text-amber-700'}`}>{pr.detail}</span>
                                <div className="flex gap-1 flex-wrap justify-center">
                                  {t.sessions.map((s, i) => {
                                    const attended = (p.completion?.attendance?.session_ids || []).includes(s.id);
                                    return (
                                      <button key={s.id} title={s.title} aria-label={`Toggle attendance for ${p.name}: ${s.title}`}
                                        onClick={() => toggleSession(p, s.id)}
                                        className={`w-6 h-6 rounded text-[10px] font-bold border ${attended ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-400 border-slate-300 hover:border-emerald-400'}`}>
                                        {i + 1}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : k === 'assessment' ? (
                              <div className="flex flex-col items-center gap-1">
                                <span className={`text-xs ${pr.met ? 'text-emerald-700 font-semibold' : 'text-slate-500'}`}>{pr.detail}</span>
                                <button onClick={() => {
                                  const v = window.prompt('Assessment score:', p.completion?.assessment?.value ?? '');
                                  if (v === null || v === '') return;
                                  recordCompletion(p.id, 'assessment', true, Number(v));
                                }} className="text-[10px] font-semibold text-teal-700 hover:underline" data-testid={`record-assessment-${p.id}`}>Record score</button>
                              </div>
                            ) : (
                              <button onClick={() => recordCompletion(p.id, k, !pr.met)} aria-label={`Toggle ${REQ_LABELS[k]} for ${p.name}`} data-testid={`toggle-${k}-${p.id}`} className="inline-flex">
                                {pr.met ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <XCircle className="w-5 h-5 text-slate-300 hover:text-amber-500" />}
                              </button>
                            )}
                            {pr.verified_by && <p className="text-[9px] text-slate-400 mt-0.5">by {pr.verified_by}</p>}
                          </td>
                        );
                      })}
                      <td className="py-2.5 px-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase ${p.eligible ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`} title={p.eligibility_reasons.join('; ')} data-testid={`eligibility-badge-${p.id}`}>
                          {p.eligible ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                          {p.override?.active ? 'Override' : p.eligible ? 'Eligible' : 'Ineligible'}
                        </span>
                        <p className="text-[10px] text-slate-400 mt-1 max-w-[160px] mx-auto">{p.eligibility_reasons[0]}</p>
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {p.recipient_approved?.approved ? (
                          <div>
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-teal-100 text-teal-800"><ShieldCheck className="w-3 h-3" /> Approved</span>
                            <p className="text-[9px] text-slate-400 mt-1">{p.recipient_approved.by} · {new Date(p.recipient_approved.at).toLocaleDateString()}</p>
                            <button onClick={() => approve([p.id], false)} className="text-[10px] font-semibold text-rose-600 hover:underline mt-0.5" data-testid={`unapprove-one-${p.id}`}>Unapprove</button>
                          </div>
                        ) : p.eligible ? (
                          <button onClick={() => approve([p.id], true)} className="px-3 py-1 text-[11px] font-bold text-white bg-teal-600 hover:bg-teal-700 rounded-full" data-testid={`approve-one-${p.id}`}>Approve</button>
                        ) : (
                          <span className="text-[11px] text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-center whitespace-nowrap">
                        <button onClick={() => editEmail(p)} title="Correct delivery email" aria-label={`Correct email for ${p.name}`} className="text-slate-400 hover:text-teal-700 p-1" data-testid={`edit-email-${p.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => doOverride(p)} title="Eligibility override (super admin, reason required)" aria-label={`Eligibility override for ${p.name}`} className={`p-1 ${p.override?.active ? 'text-violet-600' : 'text-slate-400 hover:text-violet-600'}`} data-testid={`override-${p.id}`}><ClipboardCheck className="w-4 h-4" /></button>
                        <button onClick={() => removeParticipant(p)} title="Remove participant" aria-label={`Remove ${p.name}`} className="text-slate-400 hover:text-rose-600 p-1" data-testid={`remove-participant-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---------- Trainings / Templates list ----------
  return (
    <div className="space-y-6" data-testid="certificates-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Training Certificates</h2>
          <p className="text-sm text-slate-500">Configure completion requirements, verify participants, approve recipients, and issue signed certificates</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-slate-200 overflow-hidden mr-2" role="group" aria-label="Certificates view">
            <button onClick={() => setView('trainings')} className={`px-4 py-2 text-xs font-bold ${view === 'trainings' ? 'bg-teal-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid="view-trainings-btn">Trainings</button>
            <button onClick={() => setView('templates')} className={`px-4 py-2 text-xs font-bold ${view === 'templates' ? 'bg-teal-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid="view-templates-btn">Templates ({templates.length})</button>
            <button onClick={() => setView('signers')} className={`px-4 py-2 text-xs font-bold ${view === 'signers' ? 'bg-teal-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid="view-signers-btn">Signers ({signers.length})</button>
            <button onClick={() => setView('archive')} className={`px-4 py-2 text-xs font-bold ${view === 'archive' ? 'bg-teal-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid="view-archive-btn">Archive</button>
            <button onClick={() => setView('delivery')} className={`px-4 py-2 text-xs font-bold ${view === 'delivery' ? 'bg-teal-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid="view-delivery-btn">Send Uploaded</button>
          </div>
          {view === 'trainings' ? (
            <button onClick={() => { setForm(emptyTrainingForm); setEditTraining(null); setShowForm(true); }} className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-lg" data-testid="create-training-btn">
              <Plus className="w-4 h-4" /> New Training
            </button>
          ) : view === 'archive' ? (
            <button onClick={exportArchiveCsv} className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold rounded-lg" data-testid="export-csv-btn">
              <Download className="w-4 h-4" /> Export CSV
            </button>
          ) : view === 'signers' ? (
            <button onClick={() => { setSignerForm({ name: '', title: '', organization: 'DC Early Learning Collaborative', email: '' }); setEditSigner(null); setShowSignerForm(true); }} className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold rounded-lg" data-testid="add-signer-btn">
              <UserPlus className="w-4 h-4" /> Add Signer
            </button>
          ) : view === 'delivery' ? null : (
            <label className={`inline-flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-lg cursor-pointer ${busy ? 'opacity-60 pointer-events-none' : ''}`}>
              <Upload className="w-4 h-4" /> {busy ? 'Uploading…' : 'Upload Template'}
              <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" data-testid="upload-template-input" onChange={e => { const f = e.target.files?.[0]; if (f) uploadTemplate(f); e.target.value = ''; }} />
            </label>
          )}
        </div>
      </div>

      {view === 'delivery' ? (
        <CertDeliveryView API_URL={API_URL} getAuthHeader={getAuthHeader} />
      ) : view === 'archive' ? (
        <div className="space-y-4" data-testid="archive-view">
          <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-end gap-3 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input value={archiveFilters.search} onChange={e => setArchiveFilters(f => ({ ...f, search: e.target.value }))}
                placeholder="Search name, email or certificate #" aria-label="Search archive"
                className="pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm w-72 focus:outline-none focus:ring-2 focus:ring-teal-500" data-testid="archive-search-input" />
            </div>
            <select value={archiveFilters.status} onChange={e => setArchiveFilters(f => ({ ...f, status: e.target.value }))}
              aria-label="Filter by status" className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white" data-testid="archive-status-filter">
              <option value="">All statuses</option>
              {['generated', 'awaiting_signatures', 'partially_signed', 'signed', 'approved', 'revoked'].map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
            <select value={archiveFilters.training_id} onChange={e => setArchiveFilters(f => ({ ...f, training_id: e.target.value }))}
              aria-label="Filter by training" className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white max-w-[260px]" data-testid="archive-training-filter">
              <option value="">All trainings</option>
              {trainings.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
            <span className="text-xs text-slate-400 pb-2" data-testid="archive-total">Showing {archive.certificates.length} of {archive.total} certificate{archive.total !== 1 ? 's' : ''}</span>
          </div>
          {archive.certificates.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500" data-testid="archive-empty">
              <Award className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="font-semibold text-slate-700 mb-1">No certificates found</p>
              <p className="text-sm">Try adjusting the search or filters.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                  <tr>
                    <th className="text-left py-2 px-3">Certificate #</th>
                    <th className="text-left py-2 px-3">Participant</th>
                    <th className="text-left py-2 px-3">Training</th>
                    <th className="text-center py-2 px-3">Status</th>
                    <th className="text-center py-2 px-3">Email Delivery</th>
                    <th className="text-center py-2 px-3">Issued</th>
                    <th className="text-center py-2 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {archive.certificates.map(c => (
                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`archive-row-${c.id}`}>
                      <td className="py-2.5 px-3 font-mono text-xs text-slate-700">{c.cert_number}</td>
                      <td className="py-2.5 px-3">
                        <p className="font-medium text-slate-800">{c.display_name || c.participant_name}</p>
                        <p className="text-xs text-slate-400">{c.email}</p>
                      </td>
                      <td className="py-2.5 px-3 text-xs text-slate-600">{c.training_title}</td>
                      <td className="py-2.5 px-3 text-center">
                        <span className={`inline-block px-2 py-1 rounded-full text-[10px] font-bold uppercase ${
                          c.status === 'approved' ? 'bg-indigo-100 text-indigo-800' :
                          c.status === 'signed' ? 'bg-emerald-100 text-emerald-800' :
                          c.status === 'revoked' ? 'bg-rose-100 text-rose-700' :
                          c.status === 'generated' ? 'bg-slate-100 text-slate-600' :
                          'bg-amber-100 text-amber-800'
                        }`}>{c.status.replace(/_/g, ' ')}</span>
                      </td>
                      <td className="py-2.5 px-3 text-center text-xs">
                        {c.delivery ? `${c.delivery.status}${c.delivery.attempts > 1 ? ` ×${c.delivery.attempts}` : ''}` : '—'}
                      </td>
                      <td className="py-2.5 px-3 text-center text-xs text-slate-500">{(c.created_at || '').slice(0, 10)}</td>
                      <td className="py-2.5 px-3 text-center whitespace-nowrap">
                        <button onClick={() => viewCertPdf(c)} className="text-xs font-semibold text-teal-700 hover:underline mr-3" data-testid={`archive-view-${c.id}`}>View PDF</button>
                        <button onClick={() => setSelected(c.training_id)} className="text-xs font-semibold text-slate-500 hover:underline" data-testid={`archive-open-training-${c.id}`}>Open Training</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : view === 'signers' ? (
        <div className="space-y-4" data-testid="signers-view">
          {showSignerForm && (
            <div className="bg-white border border-violet-200 rounded-xl p-4 flex items-end gap-3 flex-wrap" data-testid="signer-form">
              {[['name', 'Full Name *'], ['title', 'Title'], ['organization', 'Organization'], ['email', 'Email *']].map(([k, label]) => (
                <div key={k}>
                  <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor={`sg-${k}`}>{label}</label>
                  <input id={`sg-${k}`} data-testid={`signer-${k}-input`} value={signerForm[k]} onChange={e => setSignerForm(f => ({ ...f, [k]: e.target.value }))} className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-52 focus:outline-none focus:ring-2 focus:ring-violet-500" />
                </div>
              ))}
              <button onClick={saveSigner} data-testid="save-signer-btn" className="px-4 py-2 text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 rounded-lg">{editSigner ? 'Save Changes' : 'Add Signer'}</button>
              <button onClick={() => { setShowSignerForm(false); setEditSigner(null); }} className="px-3 py-2 text-sm text-slate-500">Cancel</button>
            </div>
          )}
          {signers.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500" data-testid="signers-empty">
              <PenTool className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="font-semibold text-slate-700 mb-1">No signers yet</p>
              <p className="text-sm">Add the people who sign certificates (e.g., Executive Director). They'll receive secure email links to sign — no admin account needed.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                  <tr>
                    <th className="text-left py-2 px-4">Name</th>
                    <th className="text-left py-2 px-4">Title</th>
                    <th className="text-left py-2 px-4">Organization</th>
                    <th className="text-left py-2 px-4">Email</th>
                    <th className="text-center py-2 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {signers.map(s => (
                    <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`signer-row-${s.id}`}>
                      <td className="py-2.5 px-4 font-medium text-slate-800">{s.name}</td>
                      <td className="py-2.5 px-4 text-slate-600">{s.title || '—'}</td>
                      <td className="py-2.5 px-4 text-slate-600">{s.organization || '—'}</td>
                      <td className="py-2.5 px-4 text-slate-600">{s.email}</td>
                      <td className="py-2.5 px-4 text-center whitespace-nowrap">
                        <button onClick={() => { setSignerForm({ name: s.name, title: s.title || '', organization: s.organization || '', email: s.email }); setEditSigner(s.id); setShowSignerForm(true); }} aria-label={`Edit ${s.name}`} className="text-slate-400 hover:text-teal-700 p-1" data-testid={`edit-signer-${s.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => deleteSigner(s)} aria-label={`Delete ${s.name}`} className="text-slate-400 hover:text-rose-600 p-1" data-testid={`delete-signer-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : view === 'templates' ? (
        templates.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500" data-testid="templates-empty">
            <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="font-semibold text-slate-700 mb-1">No certificate templates yet</p>
            <p className="text-sm">Upload a pre-designed PDF, PNG, or JPG. Its fixed design and training details are preserved exactly — you'll only place the variable fields (participant name, signatures, etc.).</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="templates-grid">
            {templates.map(tp => (
              <div key={tp.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`template-card-${tp.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <FileText className="w-6 h-6 text-teal-600" />
                  <span className="text-[10px] text-slate-400 uppercase">{tp.file_type} · {tp.orientation} · v{tp.version}</span>
                </div>
                <h3 className="font-bold text-slate-800">{tp.name}</h3>
                <p className="text-xs text-slate-500 mb-1">{(tp.fields || []).length} field{(tp.fields || []).length === 1 ? '' : 's'} placed{(tp.fields || []).some(f => f.type === 'participant_name') ? '' : ' — participant name missing'}</p>
                {tp.low_res && <p className="text-xs text-amber-700 font-semibold mb-1">⚠ Low resolution</p>}
                <div className="flex items-center gap-3 mt-3">
                  <button onClick={() => setEditorTpl(tp)} className="px-3 py-1.5 text-xs font-bold text-white bg-teal-600 hover:bg-teal-700 rounded-lg" data-testid={`edit-template-${tp.id}`}>Open Editor</button>
                  <button onClick={() => deleteTemplate(tp)} className="text-xs font-semibold text-rose-600 hover:underline" data-testid={`delete-template-${tp.id}`}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : trainings.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">
          <Award className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="font-semibold text-slate-700 mb-1">No trainings yet</p>
          <p className="text-sm">Create a training to configure its completion requirements, add participants, and manage certificates.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {trainings.map(t => (
            <div key={t.id} role="button" tabIndex={0} onClick={() => setSelected(t.id)} onKeyDown={e => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected(t.id); } }} className="text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-teal-400 hover:shadow-md transition-all cursor-pointer" data-testid={`training-card-${t.id}`}>
              <div className="flex items-start justify-between">
                <Award className="w-6 h-6 text-teal-600 mb-3" />
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-400">{t.date}</span>
                  <button onClick={(e) => { e.stopPropagation(); duplicateTraining(t); }} onKeyDown={e => e.stopPropagation()} aria-label={`Duplicate ${t.title}`} title="Duplicate this training (requirements, template & signers)" className="text-slate-500 hover:text-teal-600 p-1" data-testid={`duplicate-training-${t.id}`}>
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <h3 className="font-bold text-slate-800 mb-1">{t.title}</h3>
              <p className="text-xs text-slate-500 mb-3 line-clamp-2">{t.description || t.organization}</p>
              <div className="flex gap-4 text-xs">
                <span className="text-slate-600"><strong>{t.counts.participants}</strong> participants</span>
                <span className="text-emerald-700"><strong>{t.counts.eligible}</strong> eligible</span>
                <span className="text-teal-700"><strong>{t.counts.approved}</strong> approved</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CertificatesTab;
